export const NOTIFICATIONS_KEY = "@fahali_notifications_enabled";
const LEGACY_PUSH_KEY = "fahali_pushNotifications";

interface Storage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}

export interface NotificationPreference {
  enabled: boolean;
  loaded: boolean;
  error: string | null;
}

/** Two old switches disagreed. An explicit opt-out from either takes priority. */
export function migratedNotificationPreference(banner: string | null, push: string | null): boolean {
  if (banner === "0" || push === "false") return false;
  return banner === "1" || push === "true";
}

/** A shared snapshot makes an opt-out immediately visible to mounted banners. */
export function createNotificationPreferenceStore(storage: Storage) {
  let snapshot: NotificationPreference = { enabled: false, loaded: false, error: null };
  let loading: Promise<void> | null = null;
  let writes: Promise<void> = Promise.resolve();
  const listeners = new Set<() => void>();
  const publish = (next: NotificationPreference) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  const load = () => {
    if (!loading) {
      loading = Promise.all([storage.getItem(NOTIFICATIONS_KEY), storage.getItem(LEGACY_PUSH_KEY)])
        .then(([banner, push]) => {
          publish({ enabled: migratedNotificationPreference(banner, push), loaded: true, error: null });
        })
        .catch(() => {
          publish({ enabled: false, loaded: true, error: "Could not read notification preferences. Notifications are off." });
        });
    }
    return loading;
  };
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    load,
    async setEnabled(enabled: boolean) {
      await load();
      publish({ enabled, loaded: true, error: null });
      // Preserve write order if two mounted controls are used rapidly.
      const write = writes.then(async () => {
        try {
          await Promise.all([
            storage.setItem(NOTIFICATIONS_KEY, enabled ? "1" : "0"),
            storage.setItem(LEGACY_PUSH_KEY, enabled ? "true" : "false"),
          ]);
        } catch {
          publish({ enabled: false, loaded: true, error: "Could not save this preference. Notifications are off for this session; try again." });
          throw new Error("Could not save notification preference.");
        }
      });
      writes = write.catch(() => {});
      await write;
    },
  };
}
